import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigBool, updateConfigString } from '../sqlite/common/config';

interface IFilterMenu {
  filtersOrSorts: number;
  selectedBranchCode: string;
  techCodeCheckbox: boolean;
  todaysBookingsCheckbox: boolean;
  orderByDateDescending: boolean;
}

const initialState: IFilterMenu = {
  filtersOrSorts: 0,
  selectedBranchCode: '',
  techCodeCheckbox: false,
  todaysBookingsCheckbox: false,
  orderByDateDescending: false,
};

/// THUNKS

export const setFiltersOrSortsStoreAsync = createAsyncThunk('filtersMenu/setFiltersOrSortsStoreAsync', async (value: number, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected filter sort  number '${value}' to storage`, '[filtersMenu] setFiltersOrSortsStoreAsync');
  try {
    dispatch(setFiltersOrSortsState(value));
    await updateConfigString(ConfigName.FilterMenuFiltersOrSorts, value.toString(), false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setSelectedBranchCodeStoreAsync = createAsyncThunk<{}, string>('filtersMenu/setSelectedBranchCodeStoreAsync', async (branchCode, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected branch code '${branchCode}' to storage`, '[filtersMenu] setSelectedBranchCodeStoreAsync');
  try {
    dispatch(setSelectedBranchCodeState(branchCode));
    await updateConfigString(ConfigName.FilterMenuSelectedBranchCode, branchCode, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setTechCodeCheckboxStoreAsync = createAsyncThunk<{}, boolean>('filtersMenu/setTechCodeCheckboxStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting tech code filter '${val}' to storage`, '[filtersMenu] setTechCodeCheckboxStoreAsync');
  try {
    dispatch(setTechCodeCheckboxState(val));
    await updateConfigBool(ConfigName.FilterMenuTechCodeCheckbox, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setTodaysBookingsCheckboxStoreAsync = createAsyncThunk<{}, boolean>('filtersMenu/setTodaysBookingsCheckboxStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting todays bookings '${val}' to storage`, '[filtersMenu] setTodaysBookingsCheckboxStoreAsync');
  try {
    dispatch(setTodaysBookingsCheckboxState(val));
    await updateConfigBool(ConfigName.FilterMenuTodaysBookingsCheckbox, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setOrderByDateDescendingStoreAsync = createAsyncThunk<{}, boolean>('filtersMenu/setOrderByDateDescendingStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting order by date '${val}' to storage`, '[filtersMenu] setOrderByDateDescendingStoreAsync');
  try {
    dispatch(setOrderByDateDescendingState(val));
    await updateConfigBool(ConfigName.FilterMenuOrderByDateDescending, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const filterMenuSlice = createSlice({
  name: 'filtersMenu',
  initialState,
  reducers: {
    setFiltersOrSortsState: (state, action: PayloadAction<number>) => {
      logConsole(LogType.Info, `[filtersMenu] setFiltersOrSortsState: ${action.payload}`);
      state.filtersOrSorts = action.payload;
    },
    setTechCodeCheckboxState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[filtersMenu] setTechCodeCheckboxState: ${action.payload}`);
      state.techCodeCheckbox = action.payload;
    },
    setTodaysBookingsCheckboxState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[filtersMenu] setTodaysBookingsCheckboxState: ${action.payload}`);
      state.todaysBookingsCheckbox = action.payload;
    },
    setOrderByDateDescendingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[filtersMenu] setOrderByDateDescendingState: ${action.payload}`);
      state.orderByDateDescending = action.payload;
    },
    setSelectedBranchCodeState: (state, action: PayloadAction<string>) => {
      logConsole(LogType.Info, `[filtersMenu] setSelectedBranchCodeState: ${action.payload}`);
      state.selectedBranchCode = action.payload;
    },
  },
});

/// REDUCERS

export const { setFiltersOrSortsState, setSelectedBranchCodeState, setTechCodeCheckboxState, setTodaysBookingsCheckboxState, setOrderByDateDescendingState } = filterMenuSlice.actions;

/// CUSTOM SELECTORS

export default filterMenuSlice.reducer;
